import { UserDetails } from "../types";
import api from "./api";
import axios from "axios";
class AccountService {
    private baseUrl = "/account";

    private accessToken: string | null;
    private pwStrict: boolean | null = true;
    private activeUser: UserDetails = null;

    private listeners: ((isLoggedIn: boolean) => void)[] = [];

    subscribe = (listener: (isLoggedIn : boolean) => void) => {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter((l) => l !== listener);
        };
    }

    notifySubscribers(isLoggedIn : boolean) {
        this.listeners.forEach((listener) => listener(isLoggedIn));
    }

    constructor() {
        try {
            this.accessToken = localStorage.getItem('JWT');
            this.activeUser = JSON.parse(localStorage.getItem('User'));
        }
        catch {
            this.accessToken = null;
            this.activeUser = null;
        }
    }

    getAccessToken = () => {
        return this.accessToken;
    }

    getActiveUser = () => {
        return this.activeUser;
    }

    logInWithToken = (token: string, user: UserDetails) => {
        this.accessToken = token;
        this.activeUser = user;
        localStorage.setItem('JWT', token);
        localStorage.setItem('User', JSON.stringify(user));

        this.notifySubscribers(user != null);
    }

    logOut = () => {
        this.accessToken = "";
        this.activeUser = null;
        localStorage.removeItem('JWT');
        localStorage.removeItem('User');

        this.notifySubscribers(false);
    }
    

    logIn = async (email, password): Promise<LoginResponse> => { 
        try {
            const { data }  = await api.post(this.baseUrl + "/login", { email, password });
            const loginData = data as LoginResponse;

            localStorage.setItem('JWT', loginData.token);
            localStorage.setItem('User', JSON.stringify(loginData.userDetails));
            this.accessToken = loginData.token;
            this.activeUser = loginData.userDetails;

            this.notifySubscribers(true);
            return data as LoginResponse;
        } catch (error) {

            console.error(error);
            return null;
        }
    }


    register = async (data: RegisterRequest): Promise<RegisterResponse> => {
        try {
            const url = `${this.baseUrl}/register`;

            const formData = objectToFormData(data);
            const response = await api.post(url, formData);

            const json = response.data;
            return {
                success: json.success === true,
                error: json.success ? null : json.error || "Registration failed",
                locked: false,
            };

        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const status = error.response.status;
                return {
                    success: false,
                    error: (error.response.data as string) || "Registration failed",
                    locked: status === 429,
                }
            }

            return {
                success: false,
                error: error instanceof Error ? error.message : "Unexpected error",
                locked: false,
            };
        }
    };

    changePassword = async (currentPassword: string, newPassword: string): Promise<ChangePasswordResponse> => {
        try {
            const url = `${this.baseUrl}/change-password`;
            const { data } = await api.post(url, {currentPassword, newPassword});
            return { success: data.success };
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                console.error(error);
                return { success: false, error: error.response.data as string || "Change password failed" };
            }

            return null;
        }
    }

    deleteAccount = async (feedback: string): Promise<boolean> => {
        try {
            let url = `${this.baseUrl}/delete-account`;
            const response = await api.post(url, { feedback });
            return response.status == 200;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    forgotPassword = async (email: string): Promise<ForgotPasswordResponse> => {
        try {
            const url = `${this.baseUrl}/forgot-password`;
            const { data } = await api.post(url, { email });
            return { success: true, locked: data.locked };
        } catch (error) {
            console.error(error);
            return { success: false, locked: false };
        }
    }

    resetPassword = async (resetToken: string, email: string, newPassword: string): Promise<ResetPasswordResponse> => {
        try {
            const { data } = await api.post(`${this.baseUrl}/reset-password`, { resetToken, email, newPassword });
            return { success: data.success, tokenExpired: data.tokenExpired };
        } catch (error) {
            console.error(error);
            return { success: false, tokenExpired: false };
        }
    }


    isAuthenticated = async () => {
        try {
            const { data } = await api.get(this.baseUrl + "/check-login");
            return data.success;
        } catch (error) {

            return false;
        }
    }

    isPwStrict = async (): Promise<boolean> => {
        if (this.pwStrict != null) return this.pwStrict;

        try {
            const url = `${this.baseUrl}/pw-strict`;
            const { data } = await api.get(url);
            this.isPwStrict = data.pwStrict;
            return data;
        } catch (error) {
            console.error(error);
            return true;
        }
    }

    demoLogin = async (password) => {
        try {
            const url = `${this.baseUrl}/demo-login`;
            const { data } = await api.get(url, { params: {password}})
            return data.secret;
        } catch (error) {
            console.error(error);
            return null;
        }
    }
}

export default new AccountService();

export type LoginResponse = {
    success: boolean,
    lockedOut: boolean,
    token?: string,
    error?: string,
    userDetails?: UserDetails,
}

export type RegisterRequest = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
}

export type RegisterResponse = {
    success: boolean;
    locked: boolean;
    error?: string | null;
}

export type ChangePasswordResponse = {
    success: boolean;
    error?: string | null;
}

export type ForgotPasswordResponse = {
    success: boolean;
    locked: boolean;
}

export type ResetPasswordResponse = {
    success: boolean;
    tokenExpired: boolean;
}


function objectToFormData(obj: Record<string, any>, form?: FormData, namespace?: string): FormData {
    const formData = form || new FormData();

    for (const [key, value] of Object.entries(obj)) {
        const formKey = namespace ? `${namespace}[${key}]` : key;

        if (value instanceof File || value instanceof Blob) {
            formData.append(formKey, value);
        } else if (Array.isArray(value)) {
            value.forEach((v, i) => {
                formData.append(`${formKey}[${i}]`, v);
            });
        } else if (value !== null && typeof value === 'object') {
            objectToFormData(value, formData, formKey);
        } else if (value !== undefined && value !== null) {
            formData.append(formKey, String(value));
        }
    }

    return formData;
}

